import { useState, useRef, useEffect } from "react";
import { BarChart2, FileSpreadsheet, FileText, Calendar, Layers, Users } from "lucide-react";
import { CSVLink } from "react-csv";
import DatePicker from "react-datepicker";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import toast from "react-hot-toast";

import { selectCurrentDistro } from "../../redux/distro/distro.selector";
import { selectCurrentUser } from "../../redux/user/user.selectors";
import { sessionExpired, updateToken } from "../../redux/user/user.actions";
import { authFetch, getApiUrl, SessionExpiredError } from "../../utils/api";
import DistributionSummary from "../distribution-summary/distribution-summary.component";
import SummaryTable from "../summary/summary.component";
import StatCard from "../ui/StatCard";
import EmptyState from "../ui/EmptyState";
import { formatData, generatePDF } from "./utils";

import "react-datepicker/dist/react-datepicker.css";
import { CreditCard, Heart, Phone, DollarSign, Receipt } from "lucide-react";

const presets = [
    { key: 'lastMonth', label: 'Last Month' },
    { key: 'lastQuarter', label: 'Last Quarter' },
    { key: 'lastYear', label: 'Previous Year'},
    { key: 'ytd', label: 'YTD' },
    { key: 'custom', label: 'Custom' },
];

const DistributionSection = ({currentUser, currentDistro, onSessionExpired, onTokenRefresh}) => {
    const [activePreset, setActivePreset] = useState('custom');
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [selectedType, setSelectedType] = useState("all");
    const [groupOption, setOption] = useState("");
    const [distributionInfo, setDistributionInfo] = useState([]);
    const [summaryInfo, setSummaryInfo] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [availableTypes, setAvailableTypes] = useState({
        spending: true,
        teladoc: false,
        corehealth: false
    });
    const toastIdRef = useRef(null);
    const hasFetchedRef = useRef(false);
    const apiUrl = getApiUrl();
    const authParams = { currentUser, onSessionExpired, onTokenRefresh };

    // Fetch available types when distro changes
    useEffect(() => {
        const fetchAvailableTypes = async () => {
            if (!currentDistro || !currentUser?.token) return;
            try {
                const response = await authFetch(
                    `${apiUrl}/distribution/available-types/${currentDistro}`,
                    {},
                    authParams
                );
                if (response.ok) {
                    const data = await response.json();
                    setAvailableTypes(data);
                    // Reset selected type if current selection is no longer available
                    if (selectedType !== 'all' && !data[selectedType]) {
                        setSelectedType('all');
                    }
                }
            } catch (err) {
                if (!(err instanceof SessionExpiredError)) {
                    console.error('Failed to fetch available types:', err);
                }
            }
        };
        fetchAvailableTypes();
    }, [currentDistro, currentUser?.token, apiUrl]);

    // Set default filters when distro changes (no auto-fetch)
    useEffect(() => {
        if (!currentDistro || !currentUser?.token) return;
        if (hasFetchedRef.current) return;
        hasFetchedRef.current = true;

        // Set default grouping and dates
        const { start, end } = getPresetDates('lastMonth');
        setOption('company');
        setFromDate(start);
        setToDate(end);
        setActivePreset('lastMonth');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentDistro]);

    const portal = () => {
        const {innerWidth: width} = window;
        return width <= 500;
    };

    const formatDateForApi = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getPresetDates = (preset) => {
        const now = new Date();
        let start, end;

        switch (preset) {
            case 'lastMonth': {
                start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                end = new Date(now.getFullYear(), now.getMonth(), 0);
                break;
            }
            case 'lastQuarter': {
                const currentQuarter = Math.floor(now.getMonth() / 3);
                const lastQuarterStart = currentQuarter === 0 ? 9 : (currentQuarter - 1) * 3;
                const yearOffset = currentQuarter === 0 ? -1 : 0;
                start = new Date(now.getFullYear() + yearOffset, lastQuarterStart, 1);
                end = new Date(now.getFullYear() + yearOffset, lastQuarterStart + 3, 0);
                break;
            }
            case 'lastYear': {
                const lastYear = now.getFullYear() - 1;
                start = new Date(lastYear, 0, 1);
                end = new Date(lastYear, 11, 31);
                break;
            }
            case 'ytd': {
                start = new Date(now.getFullYear(), 0, 1);
                end = now;
                break;
            }
            default:
                return { start: null, end: null };
        }

        return { start, end };
    };

    const handlePresetClick = (preset) => {
        setActivePreset(preset);
        if (preset !== 'custom') {
            const { start, end } = getPresetDates(preset);
            setFromDate(start);
            setToDate(end);
        }
    };

    const fetchData = async () => {
        toastIdRef.current = toast.loading('Loading financials...');
        try {
            const response = await authFetch(
                `${apiUrl}/distribution/${currentDistro}/${groupOption}/${formatDateForApi(fromDate)}/${formatDateForApi(toDate)}/${selectedType}`,
                {},
                authParams
            );

            if (response.ok) {
                const data = await response.json();
                setDistributionInfo(data.distributionSummaryList);
                setSummaryInfo(data.summaryList);
                setHasSearched(true);
                toast.dismiss(toastIdRef.current);
                if (Array.isArray(data.distributionSummaryList) && data.distributionSummaryList.length === 0) {
                    toast.error("No commission report found for this date range.");
                }
            } else {
                toast.error('Failed to load financials', { id: toastIdRef.current });
            }
        } catch (err) {
            toast.dismiss(toastIdRef.current);
            if (!(err instanceof SessionExpiredError)) {
                toast.error("No commission report found for this date range.");
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (fromDate && toDate && groupOption !== "") {
            fetchData();
        } else {
            toast.error("Please select all required fields.");
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount);
    };

    const formatDateDisplay = (date) => {
        if (!date) return '';
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const getSummaryTotal = (type) => {
        const item = summaryInfo.find(s => s.type.toLowerCase().includes(type.toLowerCase()));
        return item ? parseFloat(item.amount) : 0;
    };

    const totalAmount = summaryInfo
        .filter(s => s.type.toLowerCase() !== 'total')
        .reduce((sum, s) => sum + parseFloat(s.amount || 0), 0);

    const hasData = distributionInfo && distributionInfo.length > 0;

    return (
        <div className="flex-1 px-6 md:px-12 lg:pl-12 lg:pr-24 py-6 pb-5 overflow-y-auto">
            {/* page header */}
            <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-primary/10 rounded-xl">
                    <BarChart2 className="w-6 h-6 text-primary" />
                </div>
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                        Financials
                    </h1>
                    <p className="text-sm text-gray-500">
                        Commission reports and financial summaries
                    </p>
                </div>
            </div>

            {/* filter card */}
            <div className="bg-white rounded-2xl shadow-card p-6 mb-6">
                <form onSubmit={handleSubmit}>
                    {/* preset buttons */}
                    <div className="flex flex-wrap items-center gap-2 mb-4 min-h-9">
                        {presets.map(preset => (
                            <button
                                key={preset.key}
                                type="button"
                                onClick={() => handlePresetClick(preset.key)}
                                className={`h-9 px-3 sm:px-4 text-sm sm:min-w-[100px] rounded-full font-medium transition-colors ${
                                    activePreset === preset.key
                                        ? 'bg-[#1e85e5] text-white'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                            >
                                {preset.label}
                            </button>
                        ))}
                    </div>

                    {/* filters - stack on mobile, row on desktop */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap items-end gap-3">
                        <div className="flex items-center gap-2">
                            <Layers className="w-4 h-4 text-gray-400 hidden sm:block" />
                            <select
                                className="h-10 px-4 w-full sm:w-auto border border-gray-200 rounded-xl text-sm text-gray-700 bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                onChange={(e) => setSelectedType(e.target.value)}
                                value={selectedType}
                            >
                                <option value="all">All Types</option>
                                {availableTypes.corehealth && <option value="corehealth">CoreHealth</option>}
                                {availableTypes.spending && <option value="spending">Spending Accounts</option>}
                                {availableTypes.teladoc && <option value="teladoc">Teladoc</option>}
                            </select>
                        </div>

                        <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-gray-400 hidden sm:block" />
                            <select
                                required
                                name="group_by"
                                className="h-10 px-4 w-full sm:w-auto border border-gray-200 rounded-xl text-sm text-gray-700 bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                onChange={(e) => setOption(e.target.value)}
                                value={groupOption}
                            >
                                <option value="">Group By</option>
                                <option value="company">Company</option>
                                <option value="advisor">Advisor</option>
                            </select>
                        </div>

                        <div className="flex items-center gap-2 col-span-1 sm:col-span-2 lg:col-span-1">
                            <Calendar className="w-4 h-4 text-gray-400 hidden sm:block" />
                            <DatePicker
                                className="h-10 w-full sm:w-28 lg:w-32 px-3 border border-gray-200 rounded-xl text-sm text-gray-700 bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                selected={fromDate}
                                onChange={(date) => {
                                    setFromDate(date);
                                    setActivePreset('custom');
                                }}
                                placeholderText="Start"
                                required
                                withPortal={portal()}
                                disabledKeyboardNavigation={portal()}
                            />
                            <span className="text-gray-400">to</span>
                            <DatePicker
                                className="h-10 w-full sm:w-28 lg:w-32 px-3 border border-gray-200 rounded-xl text-sm text-gray-700 bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                selected={toDate}
                                onChange={(date) => {
                                    setToDate(date);
                                    setActivePreset('custom');
                                }}
                                placeholderText="End"
                                required
                                withPortal={portal()}
                                disabledKeyboardNavigation={portal()}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={groupOption === "" || !fromDate || !toDate}
                            className="h-10 px-5 w-full flex items-center justify-center sm:w-auto bg-primary text-white rounded-xl text-sm font-medium hover:bg-primary-dark active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Apply Filter
                        </button>
                    </div>
                </form>

                <p className="text-left text-amber-600 text-sm mt-4 p-3 bg-amber-50 rounded-xl">
                    In Financials, commissions are shown by the date they were earned and are paid out the
                    following month. Note that CoreHealth commissions will only appear after they've been paid.
                </p>
            </div>

            {/* results section */}
            {hasSearched && hasData && (
                <>
                    {/* summary stats - only show cards with data */}
                    {(() => {
                        const spendingAmount = getSummaryTotal('spending');
                        const enrollmentFeeAmount = getSummaryTotal('enrollment');
                        const corehealthAmount = getSummaryTotal('corehealth');
                        const teladocAmount = getSummaryTotal('teladoc');

                        const stats = [
                            {
                                icon: DollarSign,
                                label: "Total",
                                value: formatCurrency(totalAmount),
                                sublabel: fromDate && toDate ? `${formatDateDisplay(fromDate)} - ${formatDateDisplay(toDate)}` : '',
                                variant: "primary",
                                show: true
                            },
                            {
                                icon: CreditCard,
                                label: "Spending Accounts",
                                value: formatCurrency(spendingAmount),
                                show: spendingAmount > 0
                            },
                            {
                                icon: Receipt,
                                label: "Enrollment Fee",
                                value: formatCurrency(enrollmentFeeAmount),
                                show: enrollmentFeeAmount > 0
                            },
                            {
                                icon: Heart,
                                label: "CoreHealth",
                                value: formatCurrency(corehealthAmount),
                                show: corehealthAmount > 0
                            },
                            {
                                icon: Phone,
                                label: "Teladoc",
                                value: formatCurrency(teladocAmount),
                                show: teladocAmount > 0
                            }
                        ].filter(s => s.show);

                        const gridCols = stats.length === 1 ? 'grid-cols-1' :
                            stats.length === 2 ? 'grid-cols-2' :
                            stats.length === 3 ? 'grid-cols-1 sm:grid-cols-3' :
                            stats.length === 4 ? 'grid-cols-2 lg:grid-cols-4' :
                            'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5';

                        return (
                            <div className={`grid ${gridCols} gap-4 mb-6`}>
                                {stats.map((stat, idx) => (
                                    <StatCard
                                        key={idx}
                                        icon={stat.icon}
                                        label={stat.label}
                                        value={stat.value}
                                        sublabel={stat.sublabel}
                                        variant={stat.variant}
                                    />
                                ))}
                            </div>
                        );
                    })()}

                    {/* export buttons */}
                    <div className="bg-white rounded-2xl shadow-card p-6 mb-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                                Export Report
                            </h3>
                            <div className="flex items-center gap-2">
                                <CSVLink
                                    data={formatData(distributionInfo)}
                                    filename={`Commission Report by ${groupOption}.csv`}
                                    className="h-9 px-4 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors inline-flex items-center gap-2"
                                    title="Download CSV"
                                >
                                    <FileSpreadsheet className="w-4 h-4" />
                                    <span>CSV</span>
                                </CSVLink>
                                <button
                                    type="button"
                                    onClick={() =>
                                        generatePDF(
                                            distributionInfo,
                                            summaryInfo,
                                            formatDateForApi(fromDate),
                                            formatDateForApi(toDate)
                                        )
                                    }
                                    className="h-9 px-4 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 hover:text-gray-900 transition-colors inline-flex items-center gap-2"
                                >
                                    <FileText className="w-4 h-4" />
                                    <span>PDF</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* summary table */}
                    {summaryInfo && Object.keys(summaryInfo).length > 0 && (
                        <div className="bg-white rounded-2xl shadow-card p-6 mb-6">
                            <SummaryTable summary={summaryInfo} />
                        </div>
                    )}

                    {/* distribution details */}
                    <div className="bg-white rounded-2xl shadow-card p-6 mb-6">
                        <DistributionSummary
                            key="distributionSummary"
                            distribution={distributionInfo}
                            fromDate={formatDateForApi(fromDate)}
                            toDate={formatDateForApi(toDate)}
                            currentDistro={currentDistro}
                        />
                    </div>
                </>
            )}

            {/* initial state - before search */}
            {!hasSearched && (
                <div className="bg-white rounded-2xl shadow-card p-6">
                    <EmptyState
                        icon={BarChart2}
                        title="View Financial Reports"
                        description="Select your filters above and click Apply Filter to view commission data."
                    />
                </div>
            )}

            {/* empty state - after search with no results */}
            {hasSearched && !hasData && (
                <div className="bg-white rounded-2xl shadow-card p-6">
                    <EmptyState
                        icon={BarChart2}
                        title="No financial data found"
                        description="Try adjusting your filters or date range to see financial data."
                    />
                </div>
            )}
        </div>
    );
};

const mapStateToProps = createStructuredSelector({
    currentUser: selectCurrentUser,
    currentDistro: selectCurrentDistro,
});

const mapDispatchToProps = (dispatch) => ({
    onSessionExpired: () => dispatch(sessionExpired()),
    onTokenRefresh: (token) => dispatch(updateToken(token)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DistributionSection);
